/**
 * Extensiones propias del editor de plantillas de documentos (TipTap).
 *
 * Todo lo que agregan termina como `style` en línea, con propiedades que el
 * saneador del backend ya deja pasar (`line-height`, `margin-bottom`, `width`):
 * así el PDF que arma weasyprint se ve igual que la hoja del editor. Una
 * propiedad nueva acá tiene que estar también en `_PROPIEDADES_CSS` de
 * `hr_documents/plantilla_html.py`, o se pierde al guardar sin aviso.
 */
import { Extension, Node, mergeAttributes } from '@tiptap/react'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    estiloDeParrafo: {
      /** Interlineado del párrafo (`1.5`); `null` vuelve al del documento. */
      setInterlineado: (valor: string | null) => ReturnType
      /** Espacio debajo del párrafo (`18px`); `null` vuelve al del documento. */
      setEspacioDespues: (valor: string | null) => ReturnType
    }
    imagen: {
      insertarImagen: (attrs: { src: string; alt?: string; ancho?: string }) => ReturnType
    }
  }
}

const BLOQUES = ['paragraph', 'heading']

export const EstiloDeParrafo = Extension.create({
  name: 'estiloDeParrafo',
  addGlobalAttributes() {
    return [{
      types: BLOQUES,
      attributes: {
        interlineado: {
          default: null,
          parseHTML: (el: HTMLElement) => el.style.lineHeight || null,
          renderHTML: (attrs: { interlineado?: string | null }) =>
            attrs.interlineado ? { style: `line-height: ${attrs.interlineado}` } : {},
        },
        espacioDespues: {
          default: null,
          parseHTML: (el: HTMLElement) => el.style.marginBottom || null,
          renderHTML: (attrs: { espacioDespues?: string | null }) =>
            attrs.espacioDespues ? { style: `margin-bottom: ${attrs.espacioDespues}` } : {},
        },
      },
    }]
  },
  addCommands() {
    // `updateAttributes` actúa sobre los bloques de ese tipo que toca la
    // selección: con varios párrafos seleccionados, cambian todos.
    const aplicar = (atributo: string, valor: string | null) =>
      ({ commands }: { commands: { updateAttributes: (t: string, a: Record<string, unknown>) => boolean } }) =>
        BLOQUES.map((tipo) => commands.updateAttributes(tipo, { [atributo]: valor })).some(Boolean)
    return {
      setInterlineado: (valor) => aplicar('interlineado', valor),
      setEspacioDespues: (valor) => aplicar('espacioDespues', valor),
    }
  },
})

/**
 * Imagen incrustada (`data:image/…`). Va **en línea** dentro de un párrafo, así
 * se centra o alinea con los mismos botones que el texto, sin una propiedad
 * `display`/`float` que el saneador no deja pasar.
 */
export const Imagen = Node.create({
  name: 'image',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: '' },
      ancho: {
        default: '100%',
        parseHTML: (el: HTMLElement) => el.style.width || '100%',
        renderHTML: (attrs: { ancho?: string }) => ({ style: `width: ${attrs.ancho || '100%'}` }),
      },
    }
  },
  parseHTML() {
    // Solo las incrustadas: el logo de `{{empresa.logo}}` es un chip, no esto.
    return [{ tag: 'img[src^="data:image/"]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)]
  },
  addCommands() {
    return {
      insertarImagen: (attrs) => ({ commands }) =>
        commands.insertContent({ type: this.name, attrs }),
    }
  },
})

/** Tope de una imagen incrustada, en caracteres del data URL. La plantilla
 * entera topa en 500.000 (`MAX_CARACTERES` del backend). */
export const MAX_CARACTERES_IMAGEN = 300_000

/**
 * Lee la imagen y la achica en el navegador antes de incrustarla: una foto de
 * teléfono pesa varios MB y, en base64, ella sola pasaría el tope de la
 * plantilla. 1200 px de ancho alcanzan para el ancho útil de una hoja A4.
 * Las PNG (logos, firmas escaneadas) siguen en PNG para no perder la
 * transparencia; el resto pasa a JPEG.
 */
export async function imagenComoDataUrl(archivo: File): Promise<string> {
  const original = await new Promise<string>((resolve, reject) => {
    const lector = new FileReader()
    lector.onload = () => resolve(String(lector.result))
    lector.onerror = () => reject(new Error('No se pudo leer el archivo.'))
    lector.readAsDataURL(archivo)
  })
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () => reject(new Error('El archivo no es una imagen válida.'))
    el.src = original
  })
  const esPng = archivo.type === 'image/png'
  for (const [anchoMax, calidad] of [[1200, 0.85], [900, 0.75], [600, 0.7]] as const) {
    const escala = Math.min(1, anchoMax / img.naturalWidth)
    const lienzo = document.createElement('canvas')
    lienzo.width = Math.round(img.naturalWidth * escala)
    lienzo.height = Math.round(img.naturalHeight * escala)
    lienzo.getContext('2d')?.drawImage(img, 0, 0, lienzo.width, lienzo.height)
    const url = lienzo.toDataURL(esPng ? 'image/png' : 'image/jpeg', calidad)
    if (url.length <= MAX_CARACTERES_IMAGEN) return url
  }
  throw new Error('La imagen es demasiado grande, incluso reducida. Usar una más liviana.')
}
